import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonWithLoading } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { SvgLogoLockup } from '@actual-app/components/icons/logo';
import { BigInput } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { Link } from '#components/common/Link';
import { useSetServerURL } from '#components/ServerContext';
import { useNavigate } from '#hooks/useNavigate';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';
import { loggedIn } from '#users/usersSlice';

import { Title } from './common';
import { getSaasErrorMessage } from './SaasLoginForm';

export function SaasSignup() {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const setServerURL = useSetServerURL();

  // Make sure a server URL is configured (auto-detecting the current
  // origin like useBootstrapped does), but without that hook's
  // login/bootstrap redirect dance - /signup is neither of those pages.
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    async function run() {
      const url = await send('get-server-url');
      const bootstrapped = await send('get-did-bootstrap');
      if (url == null && !bootstrapped) {
        const serverURL = window.location.origin;
        const result = await send('subscribe-needs-bootstrap', {
          url: serverURL,
        });
        if ('error' in result || !result.hasServer) {
          void navigate('/config-server');
          return;
        }
        await setServerURL(serverURL, { validate: false });
      }
      setChecked(true);
    }
    void run();
  }, [navigate, setServerURL]);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    if (email === '' || password === '' || loading) {
      return;
    }

    if (password !== confirmPassword) {
      setError('password-mismatch');
      return;
    }

    setError(null);
    setLoading(true);
    const result = await send('saas-register', {
      email,
      password,
      displayName,
    });
    setLoading(false);

    if ('error' in result) {
      setError(result.error);
      return;
    }

    const trialEndsAt = new Date(result.trialEndsAt * 1000);
    dispatch(
      addNotification({
        notification: {
          type: 'message',
          title: t('Welcome to Pesa Wise!'),
          message: t(
            'Your 30-day free trial of the full experience has started. It ends on {{date}}.',
            { date: trialEndsAt.toLocaleDateString() },
          ),
          sticky: true,
        },
      }),
    );
    void dispatch(loggedIn());
  }

  function errorMessage(err: string): string {
    if (err === 'password-mismatch') {
      return t('Passwords do not match');
    }
    return getSaasErrorMessage(t, err);
  }

  if (!checked) {
    return null;
  }

  return (
    <View style={{ maxWidth: 450, marginTop: -30, color: theme.pageText }}>
      <SvgLogoLockup
        role="img"
        aria-label={t('Pesa Wise')}
        style={{
          width: '100%',
          maxWidth: 340,
          height: 'auto',
          alignSelf: 'center',
          marginBottom: 30,
        }}
      />
      <Title text={t('Create your account')} />

      <Text
        style={{
          fontSize: 16,
          color: theme.pageTextDark,
          lineHeight: 1.4,
          marginBottom: 15,
        }}
      >
        <Trans>
          Start your free 30-day trial with the full experience — no card
          required. Your budget syncs securely across all your devices.
        </Trans>
      </Text>

      <View style={{ flexDirection: 'column', gap: '0.75rem' }}>
        <BigInput
          autoFocus
          placeholder={t('Name')}
          onChangeValue={setDisplayName}
          onEnter={onSubmit}
        />
        <BigInput
          placeholder={t('Email')}
          type="email"
          onChangeValue={setEmail}
          onEnter={onSubmit}
        />
        <BigInput
          placeholder={t('Password (at least 8 characters)')}
          type="password"
          onChangeValue={setPassword}
          onEnter={onSubmit}
        />
        <BigInput
          placeholder={t('Confirm password')}
          type="password"
          onChangeValue={setConfirmPassword}
          onEnter={onSubmit}
        />
        <ButtonWithLoading
          variant="primary"
          isLoading={loading}
          style={{
            fontSize: 15,
            ...(isNarrowWidth ? { padding: 10 } : null),
          }}
          onPress={onSubmit}
        >
          <Trans>Create account and start trial</Trans>
        </ButtonWithLoading>
      </View>

      <Text
        style={{
          fontSize: 14,
          color: theme.pageTextLight,
          marginTop: 12,
        }}
      >
        <Trans>
          Already have an account?{' '}
          <Link variant="internal" to="/login">
            Sign in
          </Link>
        </Trans>
      </Text>

      {error && (
        <Text
          style={{
            marginTop: 15,
            color: theme.errorText,
            fontSize: 15,
          }}
        >
          {errorMessage(error)}
        </Text>
      )}
    </View>
  );
}
